import {
  CommonChartProps,
  CommonHCChartExtended,
  HtmlString,
  QuestionGeoAnswerChoice,
  QuestionGeoResultData,
} from "types";
import {
  cleanCSVField,
  getHCMapLegendConfig,
  getHCMenuItems,
  getHCSubTitleConfig,
  getHCTitleConfig,
  hcChartsExportScale,
  hcChartsExportWidth,
  renderHCAreas,
} from "utils/charts/hcChartUtils";

import { VerticalAlignValue as HCVerticalAlignValue } from "highcharts";
import { downloadCSV } from "utils/download/downloadFile";
import { formatPercent } from "utils";

type getHCMapNavigationConfigProps = {
  verticalAlign?: HCVerticalAlignValue;
  x?: number;
  enabled?: boolean;
};

export const getHCMapNavigationConfig = ({
  verticalAlign = "bottom",
  x = 10,
  enabled = true,
}: getHCMapNavigationConfigProps) => {
  return {
    enabled,
    buttonOptions: {
      verticalAlign,
      x,
    },
  };
};

type getGeoHCChartSharedOptionsProps = CommonChartProps & {
  chartYSpacing?: number;
  chartXSpacing?: number;
  chartXMargin?: number;
  chartExportMarginTop?: number;
  chartSubTitleHeight?: number;
  chartTitleGap?: number;
  chartHeaderHeight?: number;
  chartExportHeight?: number;
  chartLegendExportHeight?: number;
  chartLegendExportXOffset?: number;
  chartLegendExportYOffset?: number;
  chartExportMarginBottom?: number;
  hasTitle?: boolean;
  hasSubTitle?: boolean;
};

export const getGeoHCChartSharedOptions = ({
  showTitle = false,
  showSubTitle = false,
  chartTitle,
  hasTitle = false,
  chartSubTitle,
  hasSubTitle = false,
  exportFilename,
  showExport = true,
  chartYSpacing = 0,
  chartXSpacing = 0,
  chartExportMarginTop = 0,
  chartXMargin = 0,
  chartExportMarginBottom = 0,
  chartSubTitleHeight = 0,
  chartTitleGap = 0,
  chartHeaderHeight = 0,
  chartExportHeight = 0,
  chartLegendExportHeight = 0,
  chartLegendExportXOffset = -20,
  chartLegendExportYOffset = -80,
  labelBottomLeft,
  showLegend = false,
}: getGeoHCChartSharedOptionsProps) => {
  return {
    legend: getHCMapLegendConfig({ enabled: showLegend }),

    exporting: {
      allowHTML: true,
      filename: exportFilename,
      scale: hcChartsExportScale,
      showTable: false,
      enabled: showExport,
      sourceWidth: hcChartsExportWidth,
      sourceHeight: undefined,
      buttons: {
        contextButton: {
          menuItems: getHCMenuItems(),
        },
      },
      chartOptions: {
        chart: {
          spacing: [chartYSpacing, chartXSpacing, chartLegendExportHeight, chartXSpacing],
          margin: [chartExportMarginTop, chartXMargin, chartExportMarginBottom, chartXMargin],
          height: chartExportHeight,
          events: {
            render(this: CommonHCChartExtended) {
              return renderHCAreas({
                chart: this,
                labelBottomLeft,
                showLabelBottomLeft: true,
              });
            },
          },
        },
        legend: {
          enabled: true,
          shadow: false,
          x: chartLegendExportXOffset,
          y: chartLegendExportYOffset,
        },
        title: getHCTitleConfig({
          text: chartTitle,
          enabled: hasTitle,
        }),
        subtitle: getHCSubTitleConfig({
          text: chartSubTitle,
          enabled: hasSubTitle,
          y: chartHeaderHeight - chartSubTitleHeight - chartTitleGap,
        }),
      },
    },

    mapNavigation: getHCMapNavigationConfig({}),

    title: getHCTitleConfig({ text: chartTitle, enabled: showTitle }),
    subtitle: getHCSubTitleConfig({
      text: chartSubTitle,
      enabled: showSubTitle,
      y: chartHeaderHeight - chartSubTitleHeight - chartTitleGap,
    }),

    plotOptions: {
      map: {
        showInLegend: false,
      },
    },
  };
};

type getGeoFormattedAnswerChoicePercentagesProps = {
  count?: string | number;
  answerChoices: QuestionGeoAnswerChoice[];
};

export const getGeoFormattedAnswerChoicePercentages = ({
  answerChoices,
}: getGeoFormattedAnswerChoicePercentagesProps): HtmlString => {
  return answerChoices
    .map((answerChoice) => {
      const pctFmt = formatPercent(answerChoice.pct * 100);
      return `<span class="csa-question-geo-chart-tooltip-answer">${answerChoice.label}:&nbsp;<strong>${pctFmt}</strong></span>`;
    })
    .join("<br/>");
};

/**
 * Helper function to export geo data to CSV.
 *
 * Highcharts requires a custom export function due to limitations in its built-in export options.
 * This function is triggered by the "Download CSV" button click, converts the current series data to CSV format,
 * and initiates the download of the file.
 */
export const geoDataToCSVExport = (geoData: QuestionGeoResultData[], title: string, exportFilename: string) => {
  // Collect data for CSV export
  const data = geoData.map((dataPoint) => {
    return [
      `"${dataPoint.geoLabel}"`, // e.g. "City, State" - need to escape the comma
      dataPoint.count, // total responses
      ...dataPoint.answerChoices.map((ac) => ac.count), // counts for each choice
      ...dataPoint.answerChoices.map((ac) => formatPercent(ac.pct * 100)), // pcts for each choice
    ];
  });

  // Prepare the CSV headers
  const csvHeaders = [
    title,
    "Total Responses",
    ...geoData[0].answerChoices.map((ac) => cleanCSVField(ac.label)),
    ...geoData[0].answerChoices.map((ac) => cleanCSVField(`${ac.label} %`)),
  ];

  // Prepare the CSV content header and rows
  const csvContent = [csvHeaders, ...data.map((row) => row.join(","))].join("\n");

  // Download the CSV file
  downloadCSV(csvContent, exportFilename);
};
